// Types pour les comptes rendus de séance

export interface SessionReportItem {
  id: string;
  content: string;
  isEditable: boolean;
  source?: 'manual' | 'analysis'; // Origine de l'item
  originalType?: string; // Type original de la recommandation (phytotherapie, complement, etc.)
}

export interface SessionReportSection {
  title: string;
  items: SessionReportItem[];
}

export interface SessionReport {
  id?: string;
  patient_id: string;
  child_id?: string | null;
  user_id?: string;
  accompaniment_id: string | null;
  accompaniment_title: string;
  report_date: string;
  hygiene_vie: SessionReportItem[];
  conseils_alimentaires: SessionReportItem[];
  soutiens_naturels: SessionReportItem[];
  routine_bien_etre: SessionReportItem[];
  notes: string | null;
  bach_flowers_data?: any | null;
  created_at?: string;
  updated_at?: string;
}

// Templates par type d'accompagnement
export const sessionReportTemplates: Record<string, {
  hygiene_vie: string[];
  conseils_alimentaires: string[];
  soutiens_naturels: string[];
  routine_bien_etre: string[];
}> = {
  "naturopathie-adultes": {
    hygiene_vie: [
      "Amélioration du sommeil (routine, lumière, écrans, respiration)",
      "Soutien digestif (mastication, rythme des repas, hydratation)",
      "Gestion du stress quotidien (rituels rapides)"
    ],
    conseils_alimentaires: [
      "Privilégier nourriture vivante et légère",
      "Adapter les repas au terrain (selon énergie, digestion, saison)",
      "Réduire ce qui surcharge (selon observation : sucre, produits transformés, excès stimulant, etc.)"
    ],
    soutiens_naturels: [
      "Tisanes ciblées (digestives, relaxantes, détox douces selon besoin)",
      "Hydrolats adaptés",
      "Éventuellement bourgeons / compléments doux si nécessaire"
    ],
    routine_bien_etre: [
      "Action simple quotidienne n°1",
      "Action simple quotidienne n°2",
      "Action simple hebdomadaire"
    ]
  },
  "naturopathie-enfants": {
    hygiene_vie: [
      "Rythme de sommeil régulier",
      "Hydratation + collation adaptée",
      "Activité physique ou respiration courte au quotidien"
    ],
    conseils_alimentaires: [
      "Repas structurés",
      "Limiter aliments excitants / irritants",
      "Intégrer davantage fruits/légumes adaptés à la tolérance"
    ],
    soutiens_naturels: [
      "Infusion douce (selon âge)",
      "Hydrolat adapté",
      "Rituels du soir ou du matin pour apaiser et cadrer"
    ],
    routine_bien_etre: [
      "Un petit défi bien-être pour la semaine (1 action simple)"
    ]
  },
  "stress-sommeil": {
    hygiene_vie: [
      "Extinction écrans 30–45 min avant le coucher",
      "Lumière douce",
      "Respiration 5 min ou écriture décharge",
      "Rituel d'endormissement stable"
    ],
    conseils_alimentaires: [
      "Respiration 3–6–5 pendant la journée",
      "Pauses conscientes",
      "Ancrage rapide",
      "Réduction des surstimulations"
    ],
    soutiens_naturels: [
      "Infusion relaxante",
      "Hydrolat calmant",
      "Plante du sommeil / adaptogène selon profil"
    ],
    routine_bien_etre: [
      "Objectifs et actions sur 7 ou 14 jours"
    ]
  },
  "iridologie": {
    hygiene_vie: [
      "Soutien digestif",
      "Tonification / relaxation nerveuse",
      "Amélioration élimination / détox douce",
      "Stimulation douce d'un système (foie, circulation…)"
    ],
    conseils_alimentaires: [
      "Alimentation adaptée aux besoins observés",
      "Hydratation",
      "Rythme alimentaire",
      "Gestion du stress"
    ],
    soutiens_naturels: [
      "Sélection de plantes/hydrolats en fonction du terrain (calmant, digestif, circulatoire, dépuratif…)"
    ],
    routine_bien_etre: [
      "Recommandation prioritaire n°1",
      "Recommandation prioritaire n°2",
      "Recommandation prioritaire n°3"
    ]
  },
  "fleurs-de-bach": {
    hygiene_vie: [],
    conseils_alimentaires: [],
    soutiens_naturels: [],
    routine_bien_etre: []
  }
};

// Mapping des titres d'accompagnement vers les templates
export const getTemplateKey = (accompanimentTitle: string): string => {
  const title = accompanimentTitle.toLowerCase();
  
  if (title.includes("enfant") || title.includes("adolescent") || title.includes("ado")) {
    return "naturopathie-enfants";
  }
  if (title.includes("bach") || title.includes("émotionnel") || title.includes("emotionnel")) {
    return "fleurs-de-bach";
  }
  if (title.includes("stress") || title.includes("sommeil")) {
    return "stress-sommeil";
  }
  if (title.includes("iridologie")) {
    return "iridologie";
  }
  // Par défaut : naturopathie adultes
  return "naturopathie-adultes";
};

// Vérifie si un accompagnement est de type Fleurs de Bach
export const isBachFlowersAccompaniment = (accompanimentTitle: string): boolean => {
  const title = accompanimentTitle.toLowerCase();
  return title.includes("bach") || title.includes("émotionnel") || title.includes("emotionnel");
};

export const generateDefaultReport = (accompanimentTitle: string): {
  hygiene_vie: SessionReportItem[];
  conseils_alimentaires: SessionReportItem[];
  soutiens_naturels: SessionReportItem[];
  routine_bien_etre: SessionReportItem[];
} => {
  const templateKey = getTemplateKey(accompanimentTitle);
  const template = sessionReportTemplates[templateKey] || sessionReportTemplates["naturopathie-adultes"];
  
  const createItems = (items: string[]): SessionReportItem[] => 
    items.map((content, index) => ({
      id: `${Date.now()}-${index}-${Math.random().toString(36).substr(2, 9)}`,
      content,
      isEditable: true
    }));
  
  return {
    hygiene_vie: createItems(template.hygiene_vie),
    conseils_alimentaires: createItems(template.conseils_alimentaires),
    soutiens_naturels: createItems(template.soutiens_naturels),
    routine_bien_etre: createItems(template.routine_bien_etre)
  };
};
